import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import heroHallOfFame from "@/assets/hero-hall-of-fame.jpg";
import { MarketingHeroImage } from "@/components/site/MarketingHeroImage";
import { EmphasizedTitle } from "@/components/site/EmphasizedTitle";
import { HallOfFameTimeline } from "@/components/hall-of-fame/HallOfFameTimeline";
import { listPublishedHallOfFameEntries } from "@/lib/hall-of-fame.functions";
import { HERO_IMAGE_SIZES, heroPreloadLink } from "@/lib/marketing-images";
import { heroHallOfFamePic } from "@/lib/marketing-images.hero-hall-of-fame";
import { pageHead } from "@/lib/seo";
import { m } from "@/paraglide/messages.js";

export const Route = createFileRoute("/hall-of-fame")({
  head: () => {
    const head = pageHead({
      title: m.hof_meta_title(),
      description: m.hof_meta_description(),
      path: "/hall-of-fame",
      image: heroHallOfFame,
    });
    return {
      ...head,
      links: [...(head.links ?? []), heroPreloadLink(heroHallOfFamePic)],
    };
  },
  component: HallOfFamePage,
});

function HallOfFamePage() {
  const entriesQ = useQuery({
    queryKey: ["hall-of-fame"],
    queryFn: () => listPublishedHallOfFameEntries(),
  });

  return (
    <>
      <section className="relative isolate overflow-hidden bg-ink">
        <MarketingHeroImage
          picture={heroHallOfFamePic}
          alt={m.hof_hero_image_alt()}
          sizes={HERO_IMAGE_SIZES}
          priority
          className="absolute inset-0 h-full w-full object-cover object-right opacity-55"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/88 to-ink/25" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-transparent" />
        <div className="container-page relative page-hero-pad min-h-[42svh] md:min-h-[48svh]">
          <div className="max-w-2xl">
            <span className="eyebrow">{m.hof_eyebrow()}</span>
            <h1 className="mt-5 font-display text-hero leading-[0.92] md:text-8xl">
              <EmphasizedTitle lead={m.hof_title_lead()} accent={m.hof_title_accent()} />
            </h1>
            <p className="mt-7 max-w-xl font-body text-base text-foreground/80 md:text-lg">
              {m.hof_intro()}
            </p>
          </div>
        </div>
      </section>

      <section className="container-page page-follow-pad pb-12 md:pb-16">
        {entriesQ.isLoading ? (
          <p className="text-foreground/60">{m.hof_loading()}</p>
        ) : entriesQ.isError ? (
          <p className="text-foreground/60">{m.hof_load_error()}</p>
        ) : (
          <HallOfFameTimeline entries={entriesQ.data ?? []} />
        )}
      </section>

      <section className="border-t border-white/10 bg-black/50">
        <div className="container-page flex flex-col items-start gap-5 py-14 md:flex-row md:items-center md:justify-between md:py-16">
          <div className="max-w-xl">
            <h2 className="font-display text-3xl leading-none md:text-4xl">{m.hof_cta()}</h2>
            <p className="mt-3 text-foreground/70">{m.hof_cta_body()}</p>
          </div>
          <Link to="/schedule" className="btn-ember btn-ember-hover shrink-0 text-base">
            {m.nav_book_lesson()}
            <ArrowRight className="h-5 w-5 rtl:rotate-180" />
          </Link>
        </div>
      </section>
    </>
  );
}
